import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UpdateCurrentCompanySettingsDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_COMPANIES } from "../domain";
import { ACTION_GET_CURRENT_COMPANY_SETTINGS } from "../GetCurrentCompanySettings";

import { companiesApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_UPDATE_CURRENT_COMPANY_SETTINGS =
  "UPDATE_CURRENT_COMPANY_SETTINGS";

const allowedRoles = [UserRole.Owner];

type UpdateCompanySettingsRequest = UpdateCurrentCompanySettingsDto;

const updateCurrentCompanySettings = async (
  data: UpdateCompanySettingsRequest,
): Promise<void> => {
  const response =
    await companiesApi.companiesControllerUpdateCurrentCompanySettings(data);

  return response.data;
};

export const useUpdateCurrentCompanySettings = (
  user: UserProfileDto | null,
): {
  updateCurrentCompanySettings: (data: UpdateCompanySettingsRequest) => void;
  updateCurrentCompanySettingsEnabled: boolean;
  isSuccessUpdateCurrentCompanySettings: boolean;
  isPendingUpdateCurrentCompanySettings: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_COMPANIES, ACTION_UPDATE_CURRENT_COMPANY_SETTINGS],
    mutationFn: enabled ? updateCurrentCompanySettings : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_COMPANIES, ACTION_GET_CURRENT_COMPANY_SETTINGS],
      });
    },
  });

  return {
    updateCurrentCompanySettings: mutate,
    updateCurrentCompanySettingsEnabled: enabled,
    isSuccessUpdateCurrentCompanySettings: isSuccess,
    isPendingUpdateCurrentCompanySettings: isPending,
  };
};
